import React, { lazy, Suspense, useState, useEffect } from "react";
import clsx from "clsx";
import { Element } from "react-scroll";
import makeStyles from "@material-ui/styles/makeStyles";
import Typography from "@material-ui/core/Typography";

const Overview = lazy(() => import(/* webpackChunkName: "overview" */ "./overview"));
const Content = lazy(() => import(/* webpackChunkName: "content" */ "./content"));
const Spacing = lazy(() => import(/* webpackChunkName: "spacing" */ "../spacing"));

const useStyles = makeStyles(theme => ({
	root: {
		padding: theme.spacing(10, 0, 6),
		position: "relative",
		"&:before": {
			background: theme.palette.background.body.dark,
			content: "''",
			bottom: 0,
			left: -4000,
			position: "absolute",
			top: 0,
			width: 8000,
			zIndex: -1
		},
		[theme.breakpoints.up("md")]: {
			padding: theme.spacing(10, 0, 0)
		}
	},
	spacing: {
		marginTop: -120,
		position: "relative",
		zIndex: 4,
		[theme.breakpoints.up("lg")]: {
			marginTop: -140
		}
	},
	headline: {
		color: "#FFFFFF",
		fontFamily: "'Marat-Sans-Semibold'",
		fontSize: 23,
		letterSpacing: "0.2em",
		marginBottom: theme.spacing(4),
		marginTop: theme.spacing(4),
		textAlign: "center",
		[theme.breakpoints.up("md")]: {
			marginBottom: theme.spacing(10)
		},
		[theme.breakpoints.up("lg")]: {
			marginBottom: theme.spacing(12)
		},
		[theme.breakpoints.up("xl")]: {
			marginBottom: theme.spacing(14)
		}
	},
	content: {
		marginTop: theme.spacing(8),
		[theme.breakpoints.up("lg")]: {
			marginTop: theme.spacing(10)
		},
		[theme.breakpoints.up("xl")]: {
			marginTop: theme.spacing(14)
		}
	}
}));

const DailyRoutine = ({ data, lang }) => {
	const css = useStyles();
	const [type, setType] = useState(null);
	const [contentID, setContentID] = useState(null);
	const [tabsHeadline, setTabsHeadline] = useState(null);

	let showOverview = false;
	let headline = null;
	let quicklinks = [];
	let anker = [];
	let content = []; // eslint-disable-line react-hooks/exhaustive-deps
	let contentNoOverview = [];

	data?.textcontent?.items?.filter(item => {
		if (item?.name === "headline") {
			headline = item?.text;
		}
		if (item?.name === "showOverview") {
			if (item?.text === "True") {
				showOverview = true;
			}
		}
		return null;
	});

	data?.elements?.filter(item => {
		// overview arrays

		if (item?.tag === "links") {
			quicklinks?.push(item);
		}
		if (item?.tag === "extension-daily-routine-anker") {
			anker?.push(item);
		}

		// create content array

		if (item?.tag === "extension-daily-routine-entry") {
			if (showOverview) {
				let identifier = item?.textcontent?.items.find(e => e.name === "identifier")?.text;
				if (identifier === type) {
					content?.push(item);
				}
			} else {
				content?.push(item);
			}
		}

		return null;
	});

	useEffect(() => {
		const identifier = content?.[0]?.textcontent?.items.find(
			e => e.name === "identifier"
		)?.text;
		setContentID(identifier);

		return null;
	}, [content]);

	const handleType = (type, tabsHeadline) => {
		setType(type);
		setTabsHeadline(tabsHeadline);
	};

	// create content array  -> no overview

	if (!showOverview) {
		const firstEntryID = content?.[0]?.textcontent?.items.find(
			e => e.name === "identifier"
		)?.text;

		content?.map(item => {
			const newItem = item;

			item?.textcontent?.items?.filter(item => {
				if (item?.name === "identifier") {
					if (item?.text === firstEntryID) {
						contentNoOverview?.push(newItem);
					}
				}
				return null;
			});
			return null;
		});
	}

	return (
		<div className={clsx("extension-daily-routine", css.root)}>
			<div className={css.spacing}>
				<Suspense fallback={<></>}>
					<Spacing size="small" />
				</Suspense>
			</div>
			<Typography
				className={clsx("extension-daily-routine__headline", css.headline)}
				component="p"
				variant="overline"
				dangerouslySetInnerHTML={{ __html: headline }}
			/>
			{showOverview && (
				<div className={clsx("extension-daily-routine__overview", css.overview)}>
					<Suspense fallback={<></>}>
						<Overview quicklinks={quicklinks} anker={anker} getType={handleType} lang={lang} />
					</Suspense>
				</div>
			)}
			{showOverview ? (
				<div className={clsx("extension-daily-routine__content", css.content)}>
					<Element name="scroll-content" />
					{contentID === type ? (
						<Suspense fallback={<></>}>
							<Content
								data={content}
								lang={lang}
								type={type}
								tabsHeadline={tabsHeadline}
								overview={false}
							/>
						</Suspense>
					) : (
						<></>
					)}
				</div>
			) : (
				<div className={clsx("extension-daily-routine__content", css.content)}>
					<Suspense fallback={<></>}>
						<Content
							data={contentNoOverview}
							type={type}
							tabsHeadline={tabsHeadline}
							overview={false}
						/>
					</Suspense>
				</div>
			)}
		</div>
	);
};

export default DailyRoutine;
